import { Component, OnInit, signal, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { RouterLink } from '@angular/router';
import { Router } from '@angular/router';
import { PostService, Post } from '../../../services/post';
import { environment } from '../../../../environments/environment';

@Component({
	selector: 'app-manage-posts',
	imports: [CommonModule, RouterLink],
	templateUrl: './manage-posts.html',
	styleUrl: './manage-posts.css',
	standalone: true,
})
export class ManagePosts implements OnInit {
	private blogService = inject(PostService);
	private router = inject(Router);

	blogs = signal<Post[]>([]);
	isLoading = signal<boolean>(true);
	errorMessage = signal<string>('');

	ngOnInit() {
		this.loadBlogs();
	}

	loadBlogs() {
		this.isLoading.set(true);
		this.errorMessage.set('');

		this.blogService.getAllBlogs().subscribe({
			next: (response) => {
				if (response.status && response.data) {
					this.blogs.set(response.data);
				} else {
					// If failed but not an error, show empty state
					this.blogs.set([]);
				}
				this.isLoading.set(false);
			},
			error: (error) => {
				console.error('Error loading blogs:', error);
				// Show appropriate error based on status
				if (error.status === 401) {
					this.errorMessage.set('Unauthorized. Please log in.');
				} else if (error.status === 0) {
					this.errorMessage.set('Cannot connect to server. Please make sure backend is running.');
				} else {
					// For other errors, just show empty state instead of error
					this.blogs.set([]);
				}
				this.isLoading.set(false);
			},
		});
	}

	addNewBlog() {
		this.router.navigate(['/admin/blog/add']);
	}

	editBlog(id: number) {
		this.router.navigate(['/admin/blog/edit', id]);
	}

	togglePublish(blog: Post) {
		this.blogService.togglePublish(blog.id).subscribe({
			next: (response) => {
				if (response.status) {
					this.loadBlogs();
				}
			},
			error: (error) => console.error('Error toggling publish:', error),
		});
	}

	deleteBlog(blog: Post) {
		if (confirm(`Are you sure you want to delete "${blog.title}"?`)) {
			this.blogService.deleteBlog(blog.id).subscribe({
				next: (response) => {
					if (response.status) {
						this.loadBlogs();
					}
				},
				error: (error) => console.error('Error deleting blog:', error),
			});
		}
	}

	getImageUrl(path: string): string {
		// The backend now returns full URLs via featured_image_url accessor
		return path || '/images/placeholder-blog.jpg';
	}

	formatDate(date: string): string {
		return new Date(date).toLocaleDateString('en-US', {
			year: 'numeric',
			month: 'short',
			day: 'numeric',
		});
	}

	onImageError(event: Event) {
		const img = event.target as HTMLImageElement;
		// Prevent infinite loop by checking if already using fallback
		if (!img.src.includes('data:image')) {
			// Use a data URL as fallback to prevent further errors
			img.src = 'data:image/svg+xml,%3Csvg xmlns="http://www.w3.org/2000/svg" width="400" height="300" viewBox="0 0 400 300"%3E%3Crect fill="%23eaeae7" width="400" height="300"/%3E%3Ctext fill="%235d202d" font-family="system-ui" font-size="18" x="50%25" y="50%25" text-anchor="middle" dominant-baseline="middle"%3ENo Image%3C/text%3E%3C/svg%3E';
			img.onerror = null; // Prevent further error events
		}
	}
}
